/**
 * QUAIS FATURAS DO LOTE SÃO ENVIADAS POR E-MAIL, e por que as outras não.
 *
 * ⛔ O DEFEITO QUE ABRIU ISTO (02/10/2026, faturamento real de 2026-10). O
 * resumo contava só as faturas que seriam enviadas ("Enviar 142 fatura(s)"), mas
 * o disparo recebia a SELEÇÃO INTEIRA: o botão dizia "Enviar 145 emails" e as 3
 * "ignoradas" iam junto. Não era rótulo errado, eram dois conjuntos.
 *
 * ⛔ E NINGUÉM OLHAVA O STATUS. Havia 4 faturas em Rascunho no lote (CFQ,
 * DELSFSUL, FUPAC, IPREJUN; R$ 9.138,00). A do CFQ está segurada de propósito até
 * 06/10, esperando um apostilamento para sair com valor maior: enviada, o cliente
 * receberia a cobrança errada e depois um complementar. Só não saiu porque o
 * Heitor filtrou por "Aberta" antes.
 *
 * Por isso esta função devolve UM conjunto, `enviar`, e é ele que o resumo conta
 * E o disparo recebe. A regra é de EXCLUSÃO: Rascunho e Cancelada não saem;
 * Aberta, Parcial e Paga saem. Parcial é o estado normal da FUNDETEC, que paga
 * por nota de empenho, uma de cada vez (as 3 Parciais da base são dela); o
 * financeiro deles pede segunda via para quitar o saldo. Paga também sai:
 * reenviar o documento quitado é pedido comum. Cancelada sai da lista porque
 * mandar cobrança cancelada é pior que mandar rascunho.
 *
 * A trava de verdade (nenhuma rota de envio aceitar Rascunho nem Cancelada) é
 * do backend; aqui a tela não oferece e não mente no número.
 */

export interface FaturaDoLote {
  id: number;
  clientId: number | null;
  status?: string | null;
}

export interface ClienteDoLote {
  id: number;
  emailFinancial?: string | null;
  emails?: Array<{ type?: string; email?: string | null }> | null;
  emailDeliveryType?: string | null;
}

export type TipoDeEnvio = 'nf_fatura' | 'nf_fatura_boleto' | 'nf_fatura_relatorio' | 'nf_fatura_boleto_relatorio';

export interface LoteDeEmail {
  /** O que é enviado. É o que o resumo conta e o que o disparo recebe. */
  enviar: number[];
  porTipo: Record<TipoDeEnvio, number>;
  /** Fora por status, com o status de cada uma (ex.: { Rascunho: 4, Cancelada: 1 }). */
  foraPorStatus: Record<string, number>;
  semEnvioConfigurado: number;
  semEmailFinanceiro: number;
}

/** Situações que NÃO vão por e-mail. Todo o resto (Aberta, Parcial, Paga) vai. */
export const STATUS_QUE_NAO_ENVIA: ReadonlySet<string> = new Set(['Rascunho', 'Cancelada']);

const TIPOS: TipoDeEnvio[] = ['nf_fatura', 'nf_fatura_boleto', 'nf_fatura_relatorio', 'nf_fatura_boleto_relatorio'];

export function montarLoteDeEmail(faturas: FaturaDoLote[], clientes: ClienteDoLote[]): LoteDeEmail {
  const porId = new Map(clientes.map((c) => [c.id, c]));
  const lote: LoteDeEmail = {
    enviar: [],
    porTipo: { nf_fatura: 0, nf_fatura_boleto: 0, nf_fatura_relatorio: 0, nf_fatura_boleto_relatorio: 0 },
    foraPorStatus: {},
    semEnvioConfigurado: 0,
    semEmailFinanceiro: 0,
  };
  for (const inv of faturas) {
    // Status primeiro: Rascunho fora mesmo que o cliente tenha tudo configurado.
    const status = String(inv.status || '');
    if (STATUS_QUE_NAO_ENVIA.has(status)) {
      lote.foraPorStatus[status] = (lote.foraPorStatus[status] || 0) + 1;
      continue;
    }
    const cli = inv.clientId != null ? porId.get(inv.clientId) : undefined;
    const temFinanceiro = (cli?.emails || []).some((e) => e?.type === 'Financeiro' && !!e?.email) || !!cli?.emailFinancial;
    if (!temFinanceiro) { lote.semEmailFinanceiro++; continue; }
    const tipo = (cli?.emailDeliveryType || 'nf_fatura_boleto') as string;
    if (tipo === 'nenhum' || !TIPOS.includes(tipo as TipoDeEnvio)) { lote.semEnvioConfigurado++; continue; }
    lote.porTipo[tipo as TipoDeEnvio]++;
    lote.enviar.push(inv.id);
  }
  return lote;
}

/** O texto do modal, a partir do MESMO lote que será enviado. */
export function resumoDoLoteDeEmail(l: LoteDeEmail): string {
  const partes: string[] = [];
  if (l.porTipo.nf_fatura) partes.push(`${l.porTipo.nf_fatura} NF+Fatura`);
  if (l.porTipo.nf_fatura_boleto) partes.push(`${l.porTipo.nf_fatura_boleto} com Boleto`);
  if (l.porTipo.nf_fatura_relatorio) partes.push(`${l.porTipo.nf_fatura_relatorio} com Relatorio`);
  if (l.porTipo.nf_fatura_boleto_relatorio) partes.push(`${l.porTipo.nf_fatura_boleto_relatorio} com Boleto+Relatorio`);
  let s = `Enviar ${l.enviar.length} fatura(s) por email?\n${partes.join(', ')}`;
  // Diz O QUE ficou de fora e por quê, não a regra.
  const fora = Object.entries(l.foraPorStatus);
  if (fora.length) {
    const n = fora.reduce((t, [, k]) => t + k, 0);
    s += `\n${n} não enviada(s): ${fora.map(([st, k]) => `${k} ${st}`).join(', ')}`;
  }
  if (l.semEnvioConfigurado) s += `\n${l.semEnvioConfigurado} ignorada(s) (sem envio configurado)`;
  if (l.semEmailFinanceiro) s += `\n${l.semEmailFinanceiro} ignorada(s) (sem email financeiro)`;
  return s;
}
